import { cloneDeep, isNumber, round } from "lodash";
import diceComLogo from "images/dice-com-logo.png";

import {
  APPLICATION_TAB_NAMES,
  JOB_APPLICATION_MATCH_STATUS_FORMATTED,
  JOB_APPLICATION_MATCH_SUB_STATUS_FORMATTED,
  SOCIAL_LINK_TYPES,
} from "lookup";
import { customComparator } from "utils/common";

export const getButtonText = (target, isLoading, isUpdate) => {
  switch (target) {
    case "match": {
      if (!isUpdate && isLoading) {
        return "Matching...";
      }
      if (isUpdate && isLoading) {
        return "Updating...";
      }
      if (isUpdate) {
        return "Update";
      }

      return "";
    }

    case "shortlist": {
      if (isLoading) {
        return "Shortlisting...";
      }

      return "Shortlist";
    }

    case "skip": {
      if (isLoading) {
        return "Skipping...";
      }

      return "Skip";
    }

    case "finalist": {
      if (isLoading) {
        return "Updating...";
      }

      return "";
    }

    case "removeFinalist": {
      if (isLoading) {
        return "Updating...";
      }

      return "";
    }

    case "delete": {
      if (isLoading) {
        return "Deleting...";
      }

      return "Delete";
    }

    case "reject": {
      if (isLoading) {
        return "Rejecting...";
      }

      return "";
    }

    case "apply": {
      if (isLoading) {
        return "Approving...";
      }

      return "Approve";
    }

    case "accept": {
      if (isLoading) {
        return "Accepting...";
      }

      return "Accept";
    }

    case "noActionJobTypeApp": {
      return "This User either does not have the corresponding Job Role for this Job Opportunity or has an inactive job role. If you wish to Match them first please use the Admin Tool to add or activate the Job Role for their profile.";
    }

    default: {
      return "";
    }
  }
};

export const getButtonClassName = (
  target,
  isLoading,
  isApplied,
  isDisabled
) => {
  switch (target) {
    case "match": {
      return {
        "hover:!fill-sky-500 hover:text-sky-500":
          !isLoading && !isApplied && !isDisabled,
        "animate-pulse": isLoading,
        "opacity-30": isApplied || isDisabled,
      };
    }

    case "shortlist": {
      return {
        "hover:!fill-sky-500 hover:text-sky-500": !isLoading && !isApplied,
        "animate-pulse": isLoading,
        "opacity-30": isApplied,
      };
    }

    case "skip": {
      return {
        "hover:!fill-sky-500 hover:text-sky-500": !isLoading && !isApplied,
        "animate-pulse": isLoading,
        "opacity-75": isApplied,
      };
    }

    case "delete": {
      return {
        "hover:!fill-red-500 hover:text-red-500": !isLoading && !isApplied,
        "animate-pulse": isLoading,
        "opacity-75": isApplied,
      };
    }

    case "finalist": {
      return {
        "hover:!fill-sky-500 hover:text-sky-500": !isLoading && !isApplied,
        "animate-pulse": isLoading,
        "opacity-75": isApplied,
      };
    }

    case "note": {
      return {
        "hover:!fill-sky-500 hover:text-sky-500": !isLoading && !isApplied,
        "animate-pulse": isLoading,
        "opacity-75": isApplied,
      };
    }

    case "reject": {
      return {
        "hover:!fill-red-500 hover:text-red-500": !isLoading && !isApplied,
        "animate-pulse": isLoading,
        "opacity-75": isApplied,
      };
    }

    case "apply": {
      return {
        "hover:text-green-400": !isLoading && !isApplied && !isDisabled,
        "animate-pulse": isLoading,
        "opacity-30": isApplied || isDisabled,
      };
    }

    case "accept": {
      return {
        "hover:!fill-lime-500 hover:text-lime-500": !isLoading,
        "animate-pulse": isLoading,
      };
    }

    default: {
      return "";
    }
  }
};

export const toSafeHttpUrl = (rawValue) => {
  if (!rawValue || typeof rawValue !== "string") {
    return null;
  }

  const value = rawValue.trim();
  if (!value) {
    return null;
  }

  // If the value already has a scheme, keep it; otherwise assume https.
  const candidate = /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(value)
    ? value
    : `https://${value}`;

  try {
    const url = new URL(candidate);

    if (url.protocol !== "http:" && url.protocol !== "https:") {
      return null;
    }

    return url.href;
  } catch {
    return null;
  }
};

export const getSocialProfileUrl = (type, value) => {
  switch (type) {
    case SOCIAL_LINK_TYPES.TWITTER:
      return `https://twitter.com/${value}`;
    case SOCIAL_LINK_TYPES.FACEBOOK:
      return `https://www.facebook.com/${value}`;
    case SOCIAL_LINK_TYPES.INSTAGRAM:
      return `https://www.instagram.com/${value}`;
    case SOCIAL_LINK_TYPES.LINKEDIN:
      return `https://www.linkedin.com/in/${value}`;
    case SOCIAL_LINK_TYPES.STACKOVERFLOW:
      return toSafeHttpUrl(value);
    case SOCIAL_LINK_TYPES.HASHNODE:
      return `https://hashnode.com/@${value}`;
    case SOCIAL_LINK_TYPES.GITHUB:
      return `https://github.com/${value}`;
    case SOCIAL_LINK_TYPES.PORTFOLIO:
      return toSafeHttpUrl(value);
    default:
      break;
  }
};

export const getIconForSocialType = (type) => {
  switch (type) {
    case SOCIAL_LINK_TYPES.TWITTER:
      return "twitter";
    case SOCIAL_LINK_TYPES.INSTAGRAM:
      return "instagram";
    case SOCIAL_LINK_TYPES.LINKEDIN:
      return "linkedin";
    case SOCIAL_LINK_TYPES.FACEBOOK:
      return "facebook";
    case SOCIAL_LINK_TYPES.STACKOVERFLOW:
      return "stackOverflow";
    case SOCIAL_LINK_TYPES.HASHNODE:
      return "hash";
    case SOCIAL_LINK_TYPES.GITHUB:
      return "github";
    case SOCIAL_LINK_TYPES.PORTFOLIO:
      return "website";
    default:
      break;
  }
};

export const formatSocialLinkText = (type) => {
  switch (type) {
    case SOCIAL_LINK_TYPES.TWITTER:
      return "Twitter";
    case SOCIAL_LINK_TYPES.INSTAGRAM:
      return "Instagram";
    case SOCIAL_LINK_TYPES.LINKEDIN:
      return "LinkedIn";
    case SOCIAL_LINK_TYPES.FACEBOOK:
      return "Facebook";
    case SOCIAL_LINK_TYPES.STACKOVERFLOW:
      return "Stack Overflow";
    case SOCIAL_LINK_TYPES.HASHNODE:
      return "Hashnode";
    case SOCIAL_LINK_TYPES.GITHUB:
      return "GitHub";
    case SOCIAL_LINK_TYPES.PORTFOLIO:
      return "Portfolio";
    default:
      break;
  }
};

export const prepareLocation = (location) => {
  let value = "";

  if (location) {
    if (location.cityName) {
      value = `${location.cityName}`;
    }

    if (location.stateName) {
      value = `${value.length > 0 ? `${value},` : `${value}`} ${
        location.stateName
      }`;
    }

    if (location.countryName) {
      value = `${value.length > 0 ? `${value},` : `${value}`} ${
        location.countryName
      }`;
    }
  }

  return value;
};

/** Figma list view: "City, ST - Country" */
export const prepareListViewLocation = (location, locale) => {
  if (!location) {
    return locale || "Location not specified";
  }

  const cityState = [];
  if (location.cityName) {
    cityState.push(location.cityName);
  }
  if (location.stateName) {
    cityState.push(location.stateName);
  }

  const cityStateStr = cityState.join(", ");

  if (location.countryName) {
    return cityStateStr
      ? `${cityStateStr} - ${location.countryName}`
      : location.countryName;
  }

  return cityStateStr || locale || "Location not specified";
};

const CURRENCY_SCHEMA = {
  USD: "$",
};

/**
 * Match-specific rate wins over the talent's general rate. Value and currency
 * always come from the same rate, never mixed across the two.
 */
export const pickDisplayRate = (hit) => {
  const rate = hit?.match?.rate?.value ? hit.match.rate : hit?.ratePerHour;

  if (!rate?.value) {
    return null;
  }

  return { value: rate.value, currency: rate.currency || "USD" };
};

/** Formats a rate as "$1,250/hr". */
export const prepareHourlyRate = (rate) => {
  if (!rate?.value) {
    return "Not Specified";
  }

  const symbol = CURRENCY_SCHEMA[rate?.currency] || "";
  const formatted = Number(rate.value).toLocaleString("en-US");

  return `${symbol}${formatted}/hr`;
};

/** Same format as the full profile, but resolved from a hit. */
export const prepareListViewRate = (hit) => {
  const rate = pickDisplayRate(hit);

  if (!rate) {
    return null;
  }

  return prepareHourlyRate(rate);
};

export const prepareAvailability = (availability) => {
  const AVAILABILITY_SCHEMA = {
    FULLTIME: "Full Time",
    OPENTOOFFERS: "Open To Offers",
    FULLTIMEIMMEDIATELY: "Full Time Immediately",
    PARTTIME: "Part Time",
  };

  if (!availability || !AVAILABILITY_SCHEMA[availability]) {
    return "Not Specified";
  }

  return AVAILABILITY_SCHEMA[availability];
};

export const prepareNoticePeriod = (period) => {
  if (period === 0) {
    return "None";
  } else if (!period) {
    return "Not Specified";
  } else {
    return `${period} Days`;
  }
};

export const formatTalentSource = (user) => {
  let markup = user.additionalMarkup;

  if (!user?.talentSource) {
    return null;
  }

  if (markup && isNumber(markup)) {
    markup = ` (${round(markup, 2).toFixed(2)})`;
  } else {
    markup = "";
  }

  if (user.talentSource === "dice-integration") {
    return (
      <>
        <img
          src={diceComLogo}
          title="This talent has been sourced from dice.com"
          alt="Talent sourced from Dice"
          className="h-4 w-auto inline-block align-text-top"
        />
        {markup}
      </>
    );
  }

  return user.talentSource + markup;
};

// Shared by every surface that shows who matched a talent (or, for assessments,
// who sent the assessment) so the label and empty-state stay consistent.
export const MATCHER_LABEL = "Matcher";
export const ASSESSMENT_SENDER_LABEL = "Sent By";
export const PLACEHOLDER = "-";

export const formatMatcher = (matcher) => matcher || PLACEHOLDER;
export const formatAssessmentSender = (senderEmail) =>
  senderEmail || PLACEHOLDER;

const isRejectedStatus = (status) => {
  const rejectedStatuses = new Set([
    JOB_APPLICATION_MATCH_STATUS_FORMATTED.REJECTEDBYCUSTOMER,
    JOB_APPLICATION_MATCH_STATUS_FORMATTED.REJECTEDBYMATCHER,
    JOB_APPLICATION_MATCH_STATUS_FORMATTED.REJECTEDBYMEMBER,
  ]);

  return rejectedStatuses.has(status);
};

export const createFilter = (matches, filterKey) => {
  const filter = {};
  const clonedMatches = cloneDeep(matches).sort((a, b) =>
    a.match[filterKey] < b.match[filterKey] ? -1 : 1
  );

  clonedMatches.forEach((app) => {
    if (app.match[filterKey]) {
      filter[app.match[filterKey]] = {
        label:
          JOB_APPLICATION_MATCH_STATUS_FORMATTED[app.match[filterKey]] ||
          JOB_APPLICATION_MATCH_SUB_STATUS_FORMATTED[app.match[filterKey]],
        value:
          filterKey === "status" &&
          !isRejectedStatus(
            JOB_APPLICATION_MATCH_STATUS_FORMATTED[app.match[filterKey]]
          ),
      };
    }
  });

  return filter;
};

export const tabSupportsTableView = (tabName) => {
  return (
    tabName === APPLICATION_TAB_NAMES.SEARCHRESULTS ||
    tabName === APPLICATION_TAB_NAMES.MATCHED ||
    tabName === APPLICATION_TAB_NAMES.SHORTLISTED ||
    tabName === APPLICATION_TAB_NAMES.LONGLISTED ||
    tabName === APPLICATION_TAB_NAMES.PRECANDIDATES
  );
};

export const tabSupportsMatchAgentView = (tabName) => {
  return (
    tabName === APPLICATION_TAB_NAMES.SEARCHRESULTS ||
    tabName === APPLICATION_TAB_NAMES.MATCHED ||
    tabName === APPLICATION_TAB_NAMES.SHORTLISTED ||
    tabName === APPLICATION_TAB_NAMES.LONGLISTED ||
    tabName === APPLICATION_TAB_NAMES.PRECANDIDATES
  );
};

export const tabSupportsMatchScoreSort = (tabName) => {
  return (
    tabName === APPLICATION_TAB_NAMES.SEARCHRESULTS ||
    tabName === APPLICATION_TAB_NAMES.MATCHED ||
    tabName === APPLICATION_TAB_NAMES.SHORTLISTED ||
    tabName === APPLICATION_TAB_NAMES.LONGLISTED ||
    tabName === APPLICATION_TAB_NAMES.PRECANDIDATES
  );
};

export const getCurrentRole = (hit) => {
  const rankDetailsRole = hit?.rankDetails?.content?.current_role;

  if (rankDetailsRole) {
    return rankDetailsRole;
  }

  const resumeRole = [...(hit?.resumeWorkHistory || [])]
    .sort(customComparator)
    .find((item) => item?.title)?.title;

  if (resumeRole) {
    return resumeRole;
  }

  return [...(hit?.careers || [])]
    .sort(customComparator)
    .find((item) => item?.title)?.title;
};

/**
 * Generates a checksum of the items (user cards) to display
 */
export const generateFilterChecksum = (matches, filterKeys) => {
  const values = [];

  for (const filterKey of filterKeys) {
    const keyValues = matches
      .map((m) => `${m.match?.id}_${m.match?.[filterKey]}`)
      .sort(); // So that any change in ordering / ranking does not affect the checksum

    values.push(`${filterKey}:${keyValues.join(",")}`);
  }

  return values.join("||");
};
